import Badge, { type BadgeTone } from '@/components/base/Badge';
import Button from '@/components/base/Button';
import ProgressBar from '@/components/base/ProgressBar';
import { highestRisk, roleCoverage } from '@/utils/roles';
import { approvalCeilings } from '@/mocks/roles';
import type { Permission, PermissionGroup, PermissionRisk, RoleRecord } from '@/types/portal';

interface RoleSummaryHeaderProps {
  role: RoleRecord;
  permissions: Permission[];
  catalog: PermissionGroup[];
  ceiling: string;
  onCeiling: (value: string) => void;
  canManage: boolean;
  dirty: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const riskTone: Record<PermissionRisk, BadgeTone> = {
  Standard: 'neutral',
  Sensitive: 'secondary',
  Critical: 'primary',
};

export default function RoleSummaryHeader({
  role,
  permissions,
  catalog,
  ceiling,
  onCeiling,
  canManage,
  dirty,
  onSave,
  onCancel,
}: RoleSummaryHeaderProps) {
  const effective: RoleRecord = { ...role, permissions };
  const coverage = roleCoverage(effective, catalog);
  const risk = highestRisk(effective, catalog);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-lg font-semibold text-foreground-950">{role.name}</h2>
            {role.isSystem ? <Badge tone="outline">System role</Badge> : <Badge tone="secondary">Custom role</Badge>}
            <Badge tone={riskTone[risk]} icon="ri-shield-check-line">
              {risk} access
            </Badge>
          </div>
          <p className="mt-1.5 max-w-2xl text-sm text-foreground-600">{role.description}</p>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
                Members
              </p>
              <p className="mt-1 text-sm font-medium text-foreground-900">
                {role.memberCount} user{role.memberCount === 1 ? '' : 's'}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
                Permissions granted
              </p>
              <p className="mt-1 text-sm font-medium text-foreground-900">
                {coverage.granted} of {coverage.total}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-label uppercase tracking-wider text-foreground-400">
                Approval ceiling
              </p>
              <div className="relative mt-1">
                <span className="sr-only">Approval ceiling</span>
                <select
                  value={ceiling}
                  disabled={!canManage}
                  onChange={(event) => onCeiling(event.target.value)}
                  className="h-8 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-800 focus:border-primary-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {(approvalCeilings.includes(ceiling) ? approvalCeilings : [ceiling, ...approvalCeilings]).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col items-stretch gap-3 lg:w-56">
          <div className="rounded-md border border-background-200 bg-background-100 px-4 py-3">
            <div className="flex items-center justify-between text-xs text-foreground-500">
              <span>Catalog coverage</span>
              <span className="font-medium text-foreground-800">{coverage.percent}%</span>
            </div>
            <ProgressBar
              className="mt-2"
              value={coverage.percent}
              tone={risk === 'Critical' ? 'primary' : risk === 'Sensitive' ? 'secondary' : 'accent'}
            />
          </div>
          {canManage && dirty ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={onCancel}>
                Cancel
              </Button>
              <Button size="sm" icon="ri-save-3-line" className="flex-1" onClick={onSave}>
                Save
              </Button>
            </div>
          ) : (
            <p className="text-center text-[11px] text-foreground-400">
              {canManage ? 'No unsaved changes' : 'Read-only for your role'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}